import {Box, Button, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';

import {Refund} from 'domain/usecases';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {EmptySearchState, SentinelLoading} from 'presentation/components';
import {useGetPaginatedItemsReactQuery} from 'presentation/pages/private/hooks/useGetPaginatedItemsReactQuery';
import {FailureAlert} from 'presentation/pages/private/refund/components';
import {RefundCard} from 'presentation/pages/private/refund/components/RefundCard';
import {RefundStatusDrawer} from 'presentation/pages/private/refund/components/RefundStatusDrawer';
import {useConsultRefund} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import {useRefundStatusDrawer} from '../../../../hooks/useRefundStatusDrawer';
import {getConsultRefund} from '../../service/getConsultRefund';
import {StyledGrid} from '../ListStep/styles';

const {goBack} = translations['pt-br'].commons;
const {emptyRefundTitle, emptyRefundDescription, foundResults} =
	translations['pt-br'].privateFlow.consultRefund;

export function RefundCardList() {
	const {consultEndDate, consultInitialDate, consultProtocolNumber, handleResetQuery} =
		useConsultRefund();
	const theme = useTheme();

	const {
		items,
		loadingItemsList,
		itemsListIsEmpty,
		itemsListHasError,
		totalItems,
		loadMore,
		refresh,
	} = useGetPaginatedItemsReactQuery<Refund>({
		queryKey: [
			QUERY_KEYS.REFUND_CONSULT,
			consultProtocolNumber,
			consultInitialDate,
			consultEndDate,
		],
		queryFn: ({page}) =>
			getConsultRefund({
				protocol: consultProtocolNumber,
				startDate: consultInitialDate,
				endDate: consultEndDate,
				page,
				limit: 12,
			}),
		queryOptions: {
			enabled: !!consultProtocolNumber || (!!consultInitialDate && !!consultEndDate),
			refetchOnWindowFocus: false,
		},
	});

	const {
		openDrawerState,
		onOpenDrawer,
		onCloseDrawer,
		getRefundCardData,
		refundDataSate,
		footer,
		chipColor,
	} = useRefundStatusDrawer();

	if (itemsListHasError) {
		return (
			<StyledGrid flex={1}>
				<Stack gridColumn="1/-1" gridRow="1/-1" justifyContent="center" alignSelf="center">
					<FailureAlert refetch={refresh} />
				</Stack>
			</StyledGrid>
		);
	}

	if (itemsListIsEmpty) {
		return (
			<Box gridColumn="1/-1">
				<EmptySearchState
					title={emptyRefundTitle}
					description={emptyRefundDescription}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_REEMBOLSO_ENCONTRADO}>
					<Button variant="outlined" size="large" onClick={handleResetQuery}>
						{goBack}
					</Button>
				</EmptySearchState>
			</Box>
		);
	}

	const totalResults = totalItems ? totalItems.toString().padStart(2, '0') : 0;

	return (
		<Fragment>
			<RefundStatusDrawer
				title={refundDataSate?.protocol ?? ''}
				protocolCode={String(refundDataSate?.protocolCode ?? '')}
				chip={{
					...footer,
					chipColor,
				}}
				openDrawer={openDrawerState}
				onOpenDrawer={onOpenDrawer}
				onCloseDrawer={onCloseDrawer}
			/>

			{!loadingItemsList && items ? (
				<Typography color={theme.palette.customInfo.shades?.['160p']}>
					<b>{totalResults}</b> {foundResults}
				</Typography>
			) : null}

			<StyledGrid>
				{items.map((item, index) => (
					<RefundCard key={index} data={item} onViewMoreClick={getRefundCardData} />
				))}
			</StyledGrid>
			<SentinelLoading
				minHeight="200px"
				onVisible={() => {
					if (items.length > 0) {
						loadMore();
					}
				}}
				isLoading={loadingItemsList}
			/>
		</Fragment>
	);
}
